/* eslint-disable @typescript-eslint/no-explicit-any */
import DeleteFormModal from './modal/delete_form';
import { duplicateFormThunk } from '../../redux/thunks/form';
import { formatDateWithTime } from '../../helper/index';
import { FormObject } from '../../params/redux/form';
import ImageClass from '../../assets';
import { parseFormConfigData } from '../../helper/form_builder';
import ShareFormModal from './modal/share_form';
import { useParams } from 'react-router';
import { useState } from 'react';
import { AppDispatch, RootState } from '../../redux/store';
import { Avatar, Box, IconButton, Typography } from '@mui/material';
import { brand, green, orange, red } from '../../theme/themePrimitives';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel, GridSortCellParams } from '@mui/x-data-grid';
import { DeleteOutline, EditOutlined, FilterNoneOutlined, ShareOutlined } from '@mui/icons-material';
import { settingCreateFormFlag, settingFormFieldObject, settingSelectedForm, settingSelectedFormFlag } from '../../redux/slices/form';
import { useDispatch, useSelector } from 'react-redux';

interface FormsRow {
    id: string;
    form: string;
    status: 'active' | 'inactive' | 'draft' | 'deleted';
    submitted: number;
    createdOn: string;
    timestamp: number;
    timestampForEdit: number;
    editedOn: string;
    readOnly: boolean;
    type: 'internal' | 'external' | 'snapps' | 'elementor' | 'custom' | 'snapps-manual' | 'elementor-manual';
    createdBy: number;
}

const FormTableComponent = ({ searched, sort }: { searched: string, sort: boolean }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [showShareModal, setShowShareModal] = useState<boolean>(false);
    const { forms, assignedForms, filters } = useSelector((state: RootState) => state.form);
    const { currentUser } = useSelector((state: RootState) => state.app);

    const filteredForms = [...forms, ...assignedForms].filter((form: FormObject) => {
        if (filters.isDeleted) {
            return form.status === 'deleted';
        } else if (filters.isInternal || filters.isExternal) {
            const includeInternal = filters.isInternal && form.type === 'internal' && form.status !== 'deleted';
            const includeExternal = filters.isExternal && form.type === 'external' && form.status !== 'deleted';
            return includeInternal || includeExternal;
        }
        return ((form.type === 'internal' || form.type === 'external' || form.type === 'elementor-manual' || form.type === 'snapps-manual') && form.status !== 'deleted');
    }).filter((form: FormObject) => {
        const title = (form.title || '').trim().toLowerCase();
        const { status } = form;
        return (
            title.includes(searched.toLowerCase()) ||
            status.toLowerCase().includes(searched.toLowerCase())
        );
    });
    const sortedForms = sort
        ? [...filteredForms].sort((a, b) => a.title.localeCompare(b.title))
        : filteredForms;

    const rows: FormsRow[] = sortedForms
    .filter((form: FormObject) => form.type !== 'snapps' && form.type !== 'elementor' && form.type !== 'custom')
    .map((form: FormObject, index: number) => ({
        id: `${form.id}-${index}`,
        form: form.title,
        status: form.status,
        submitted: typeof (form.submissions) === 'number' ? form.submissions : 0,
        type: form.type,
        createdOn: form.created_at,
        timestamp: new Date(form.created_at).getTime(),
        timestampForEdit: new Date(form.updated_at).getTime(),
        editedOn: form.updated_at,
        createdBy: form.user_id,
        readOnly: form.readOnly
    })) || [];

    const handleDeleteForm = (form: FormsRow) => {
        const selectedForm = forms.find((item: FormObject) => item.id === parseInt(form.id.split('-')[0], 10));
        dispatch(settingSelectedForm(selectedForm));
        setShowDeleteModal(true);
    };

    const handleDuplicateForm = async (form: FormsRow) => {
        try {
            await dispatch(duplicateFormThunk({ form_id: parseInt(form.id.split('-')[0], 10), client_id: id ? parseInt(id as string, 10) : 0 })).unwrap();
        } catch (e) {
            // catch errors
        }
    };

    const handleShareForm = (form: FormsRow) => {
        const selectedForm = forms.find((item: FormObject) => item.id === parseInt(form.id.split('-')[0], 10));
        dispatch(settingSelectedForm(selectedForm));
        setShowShareModal(true);
    };

    const editFormHandler = async (form: FormsRow) => {
        const selectedForm = forms.find((item: FormObject) => item.id === parseInt(form.id.split('-')[0], 10));
        const formField = await parseFormConfigData(selectedForm as FormObject);
        dispatch(settingSelectedForm(selectedForm));
        dispatch(settingFormFieldObject(formField));
        dispatch(settingCreateFormFlag(true));
    };

    const onClickFormDetail = (form: FormsRow) => {
        const selectedForm = forms.find((item: FormObject) => item.id === parseInt(form.id.split('-')[0], 10));
        dispatch(settingSelectedForm(selectedForm));
        dispatch(settingSelectedFormFlag(true));
    };

    const columns: GridColDef<FormsRow>[] = [
        {
            field: 'form',
            headerName: 'FORM',
            flex: 1,
            renderCell: (params: GridRenderCellParams<FormsRow, string>) => <Box marginY={params.row.type === 'external' || params.row.type === 'elementor-manual' || params.row.type === 'snapps-manual' ? '0' : '10px'} onClick={() => onClickFormDetail(params.row)} sx={{ cursor: 'pointer' }}>
                <Typography sx={{ cursor: 'pointer' }} variant="subtitle1" fontWeight="600" color={brand[300]}>{params.value}</Typography>
                {params.row.type === 'external' && <Box display="flex" gap={1}>
                    <Avatar src={ImageClass.facebookLogo} alt="Facebook Logo" sx={{ height: 20, width: 20 }} />
                    <Typography sx={{ cursor: 'pointer' }} variant="body2" fontWeight="600" color={brand[300]}>[External Form]</Typography>
                </Box>}
                {params.row.type === 'elementor-manual' && <Box display="flex" gap={1}>
                    <Avatar src={ImageClass.elementorLogo} alt="Elementor Logo" sx={{ height: 20, width: 20 }} />
                    <Typography sx={{ cursor: 'pointer' }} variant="body2" fontWeight="600" color={brand[300]}>[Elementor Form]</Typography>
                </Box>}
                {params.row.type === 'snapps-manual' && <Box display="flex" gap={1}>
                    <Avatar src={ImageClass.snappsLogo} alt="Snapps Logo" sx={{ height: 20, width: 20 }} />
                    <Typography sx={{ cursor: 'pointer' }} variant="body2" fontWeight="600" color={brand[300]}>[Snapps Form]</Typography>
                </Box>}
            </Box>
        },
        { field: 'status', headerName: 'STATUS', flex: 1, renderCell: (params: GridRenderCellParams<FormsRow, string>) => <Box marginY="14px" bgcolor={params.value === 'active' ? green[100] : params.value === 'deleted' ? red[100] : orange[100]} color={params.value === 'active' ? green[300] : params.value === 'deleted' ? red[300] : orange[300]} display="flex" alignItems="center" width="max-content" borderRadius={1} px={2} paddingY="2px"><Typography variant="body1" fontWeight="bold">{params.value?.toLocaleUpperCase()}</Typography></Box> },
        { field: 'submitted', headerName: 'FORMS SUBMITTED', flex: 1 },
        {
            field: 'createdOn',
            headerName: 'CREATED ON',
            flex: 1,
            sortComparator: (v1: any, v2: any, param1: GridSortCellParams<FormsRow>, param2: GridSortCellParams<FormsRow>) => {
                            const row1 = rows.find((row) => row.id === param1.id);
                            const row2 = rows.find((row) => row.id === param2.id);
                            return (row1?.timestamp || 0) - (row2?.timestamp || 0);
                        },
            renderCell: (params: GridRenderCellParams<FormsRow, string>) => <Box marginY="14px"><Typography variant="body1">{params.value && formatDateWithTime(params.value)}</Typography></Box>
        },
        {
            field: 'editedOn',
            headerName: 'EDITED ON',
            flex: 1,
            sortComparator: (v1: any, v2: any, param1: GridSortCellParams<FormsRow>, param2: GridSortCellParams<FormsRow>) => {
                const row1 = rows.find((row) => row.id === param1.id);
                const row2 = rows.find((row) => row.id === param2.id);
                return (row1?.timestampForEdit || 0) - (row2?.timestampForEdit || 0);
            },
            renderCell: (params: GridRenderCellParams<FormsRow, string>) => <Box marginY="14px"><Typography variant="body1">{params.value && formatDateWithTime(params.value)}</Typography></Box>
        },
        {
            field: 'id',
            headerName: 'ACTIONS',
            flex: 1,
            renderCell: (params: GridRenderCellParams<FormsRow, number>) => <Box>
                {!params.row.readOnly && params.row.type !== 'external' && params.row.type !== 'elementor-manual' && params.row.type !== 'snapps-manual' && params.row.status !== 'deleted' && currentUser?.id === params.row.createdBy && <IconButton size="medium" color="primary" onClick={() => editFormHandler(params.row)}>
                    <EditOutlined />
                </IconButton>}
                {!params.row.readOnly && params.row.type !== 'external' && params.row.status !== 'deleted' && currentUser?.id === params.row.createdBy && <IconButton size="medium" color="primary" onClick={() => handleDuplicateForm(params.row)}>
                    <FilterNoneOutlined />
                </IconButton>}
                {!params.row.readOnly && params.row.status !== 'deleted' && currentUser?.id === params.row.createdBy && <IconButton size="medium" color="error" onClick={() => handleDeleteForm(params.row)}>
                    <DeleteOutline />
                </IconButton>}
                {params.row.status === 'active' && params.row.type !== 'external' && <IconButton size="medium" color="primary" onClick={() => handleShareForm(params.row)}>
                    <ShareOutlined />
                </IconButton>}
            </Box>
        }
    ];
    return <Box height="60vh" width="100%" mt={2}>
        <DataGrid
            rows={rows as FormsRow[]}
            columns={columns}
            initialState={{
                pagination: {
                    paginationModel: {
                        pageSize: 20
                    }
                }
            }}
            rowSelectionModel={selectedRows}
            pageSizeOptions={[5, 10, 20]}
            onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => setSelectedRows(newSelection)}
            sx={{
                '& .MuiDataGrid-row--borderBottom': {
                    backgroundColor: `${brand[50]} !important`
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold'
                },
                '& .MuiDataGrid-columnHeaders': {
                    fontWeight: 'bold'
                },
                '& .MuiDataGrid-cell': {
                    fontSize: '14px'
                },
                'width': '100%',
                'overflowX': 'hidden'
            }}
        />
        {showDeleteModal && <DeleteFormModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} />}
        {showShareModal && <ShareFormModal open={showShareModal} onClose={() => setShowShareModal(false)} />}
    </Box>;
};

export default FormTableComponent;